import React, { useState, Fragment } from 'react';
import { EditIcon, CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon, PackageIcon } from 'lucide-react';
import { CatalogSchema, CatalogProduct, CatalogField } from '../CatalogsManagement';
interface ProductTableProps {
  products: CatalogProduct[];
  schema: CatalogSchema | null;
  onEdit: (product: CatalogProduct) => void;
  onToggleStatus: (productId: string) => void;
  locale: string;
}
export function ProductTable({
  products,
  schema,
  onEdit,
  onToggleStatus,
  locale
}: ProductTableProps) {
  // State for expanded rows
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  // Toggle row expansion
  const toggleRowExpand = (productId: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };
  // Format custom field value based on its type
  const formatCustomFieldValue = (field: CatalogField, value: any) => {
    if (value === undefined || value === null) return '-';
    switch (field.dataType) {
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'date':
        return new Date(value).toLocaleDateString(locale);
      case 'currency':
        return typeof value === 'object' && value.amount && value.currency ? formatCurrency(value.amount, value.currency) : value;
      default:
        return value.toString();
    }
  };
  return <div className="bg-white shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-12427">
      {products.length === 0 ? <div className="text-center py-12" data-id="element-12428">
          <PackageIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-12429" />
          <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-12430">
            No products found
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-12431">
            Get started by adding a new product or adjusting your filters.
          </p>
        </div> : <table className="min-w-full divide-y divide-gray-200" data-id="element-12432">
          <thead className="bg-gray-50" data-id="element-12433">
            <tr data-id="element-12434">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12435">
                Product
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12436">
                Vendor SKU
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12437">
                Price
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12438">
                Stock
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12439">
                Manufacturer
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12440">
                Category
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12441">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12442">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-12443">
            {products.map(product => <Fragment key={product.id} data-id="element-12444">
                <tr className={expandedRows[product.id] ? 'bg-gray-50' : 'hover:bg-gray-50'} data-id="element-12445">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12446">
                    <div className="flex items-center" data-id="element-12447">
                      <button onClick={() => toggleRowExpand(product.id)} className="mr-2 text-gray-400 hover:text-gray-500" title={expandedRows[product.id] ? 'Collapse' : 'Expand'} data-id="element-12448">
                        {expandedRows[product.id] ? <ChevronUpIcon className="h-4 w-4" data-id="element-12449" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-12450" />}
                      </button>
                      <div className="text-sm font-medium text-indigo-600" data-id="element-12451">
                        {product.name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-12452">
                    {product.vendorSku}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-12453">
                    {formatCurrency(product.price, product.currency)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-12454">
                    {product.availableStock > 0 ? <span className="text-green-600" data-id="element-12455">
                        {product.availableStock}
                      </span> : <span className="text-red-600" data-id="element-12456">Out of stock</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-12457">
                    {product.manufacturer}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-12458">
                    {product.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12459">
                    {product.isActive ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800" data-id="element-12460">
                        <CheckIcon className="h-4 w-4 mr-1" data-id="element-12461" />
                        Active
                      </span> : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800" data-id="element-12462">
                        <XIcon className="h-4 w-4 mr-1" data-id="element-12463" />
                        Inactive
                      </span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-12464">
                    <div className="flex justify-end space-x-2" data-id="element-12465">
                      <button onClick={() => onEdit(product)} className="text-indigo-600 hover:text-indigo-900" title="Edit Product" data-id="element-12466">
                        <EditIcon className="h-4 w-4" data-id="element-12467" />
                      </button>
                      <button onClick={() => onToggleStatus(product.id)} className={product.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'} title={product.isActive ? 'Deactivate' : 'Activate'} data-id="element-12468">
                        {product.isActive ? <XIcon className="h-4 w-4" data-id="element-12469" /> : <CheckIcon className="h-4 w-4" data-id="element-12470" />}
                      </button>
                    </div>
                  </td>
                </tr>
                {/* Expanded row with custom attributes and additional details */}
                {expandedRows[product.id] && <tr data-id="element-12471">
                    <td colSpan={8} className="px-6 py-4 bg-gray-50 border-b border-gray-200" data-id="element-12472">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-12473">
                        <div data-id="element-12474">
                          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-12475">
                            Product Details
                          </h4>
                          <dl className="grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-12476">
                            {product.hsCode && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-12477">
                                  HS Code
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-12478">
                                  {product.hsCode}
                                </dd>
                              </>}
                            {product.warranty && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-12479">
                                  Warranty
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-12480">
                                  {product.warranty}
                                </dd>
                              </>}
                            <dt className="text-sm font-medium text-gray-500" data-id="element-12481">
                              Active From
                            </dt>
                            <dd className="text-sm text-gray-900" data-id="element-12482">
                              {new Date(product.activeFrom).toLocaleDateString(locale)}
                            </dd>
                            {product.activeTo && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-12483">
                                  Active To
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-12484">
                                  {new Date(product.activeTo).toLocaleDateString(locale)}
                                </dd>
                              </>}
                          </dl>
                        </div>
                        <div data-id="element-12485">
                          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-12486">
                            Packaging
                          </h4>
                          <dl className="grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-12487">
                            {product.outerPackQty && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-12488">
                                  Outer Pack Qty
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-12489">
                                  {product.outerPackQty} {product.outerPackUnit}
                                </dd>
                              </>}
                            {product.innerPackQty && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-12490">
                                  Inner Pack Qty
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-12491">
                                  {product.innerPackQty} {product.innerPackUnit}
                                </dd>
                              </>}
                          </dl>
                        </div>
                        <div data-id="element-12492">
                          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-12493">
                            Inventory
                          </h4>
                          <dl className="grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-12494">
                            <dt className="text-sm font-medium text-gray-500" data-id="element-12495">
                              Available
                            </dt>
                            <dd className="text-sm text-gray-900" data-id="element-12496">
                              {product.availableStock}
                            </dd>
                            {product.backordered !== undefined && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-12497">
                                  Backordered
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-12498">
                                  {product.backordered}
                                </dd>
                              </>}
                            {product.qtyOrdered !== undefined && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-12499">
                                  Ordered
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-12500">
                                  {product.qtyOrdered}
                                </dd>
                              </>}
                            {product.qtyInvoiced !== undefined && <>
                                <dt className="text-sm font-medium text-gray-500" data-id="element-12501">
                                  Invoiced
                                </dt>
                                <dd className="text-sm text-gray-900" data-id="element-12502">
                                  {product.qtyInvoiced}
                                </dd>
                              </>}
                          </dl>
                        </div>
                      </div>
                      {/* Custom attributes */}
                      {schema && schema.fields.length > 0 && Object.keys(product.customAttributes).length > 0 && <div className="mt-4" data-id="element-12503">
                            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-12504">
                              Custom Attributes
                            </h4>
                            <dl className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2" data-id="element-12505">
                              {schema.fields.map(field => <Fragment key={field.id} data-id="element-12506">
                                  <dt className="text-sm font-medium text-gray-500" data-id="element-12507">
                                    {field.name}
                                  </dt>
                                  <dd className="text-sm text-gray-900" data-id="element-12508">
                                    {formatCustomFieldValue(field, product.customAttributes[field.key])}
                                  </dd>
                                </Fragment>)}
                            </dl>
                          </div>}
                    </td>
                  </tr>}
              </Fragment>)}
          </tbody>
        </table>}
    </div>;
}